import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { getToken } from "utils/getToken";
import useIsTablet from "hooks/useIsTablet";
import { Box, Typography } from "@mui/material";
import CustomInput from "components/Input";
import CustomSelectComponent from "components/Select";
import { CustomSwitch } from "components/AdminListedDomainsTab";
import { LocalizationProvider } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CustomDatePicker } from "components/StandartSets";
import TimePicker from "components/TimePicker";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import Calendar from "assets/icons/Calendar";
import CustomButton from "components/Button";
import GlobalNotificationPreview from "components/AdminNotifications/GlobalNotificationPreview";
import GlobalNotificationsImagesModal from "components/AdminNotifications/GlobalNotificationsImagesModal";
import DuckHero from "assets/duckHero.png";
import DuckShekel from "assets/duckHeroShekel.png";
import DuckMoney from "assets/imageAuth.png";
import DuckUmbrella from "assets/duckUmbrella.png";
import DuckPencil from "assets/DuckPencil.png";
import DuckJew from "assets/duckJew.png";
import DuckDoctor from "assets/duckDoctor.png";
import DuckGengstar from "assets/duckGengstar.png";
import DuckGengstar2 from "assets/duckGengstar2.png";
import DuckSeaman from "assets/duckSeaman.png";
import useFetch from "hooks/useFetch";
import { toast } from "react-toastify";
import { IGlobalNotification } from "interfaces/Notifications";
import GlobalNotificationsList from "components/AdminNotifications/GlobalNotificationsList";
import IndividualNotifications from "components/AdminNotifications/IndividualNotifications";

const NotificationsPage = () => {
  const { t } = useTranslation();
  const token = getToken();
  const isTablet = useIsTablet();
  const [title, setTitle] = useState<string>("Default title...");
  const [description, setDescription] = useState<string>(
    "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed ullamcorper est ut sem suscipit euismod. Suspendisse potenti. Donec cursus metus sollicitudin ex eleifend faucibus sollicitudin tempor velit. Maecenas id ultricies lectus."
  );
  const [background, setBackground] = useState<string>("#FFFFFF");
  const [isButtonVisible, setIsButtonVisible] = useState<boolean>(false);
  const [buttonText, setButtonText] = useState<string>("Default button label");
  const [buttonLink, setButtonLink] = useState<string>("");
  const [selectedTime, setSelectedTime] = useState<Dayjs | null>(null);
  const [isImagesModalOpen, setIsImagesModalOpen] = useState<boolean>(false);
  const [previewImage, setPreviewImage] = useState<File | string>(DuckHero);
  const [globalNotifications, setGlobalNotifications] = useState<IGlobalNotification[]>([]);
  const previewImagesArray = [
    DuckHero,
    DuckShekel,
    DuckMoney,
    DuckGengstar2,
    DuckSeaman,
    DuckJew,
    DuckDoctor,
    DuckGengstar,
    DuckUmbrella,
    DuckPencil,
  ];

  const backgroudOptions = [
    { label: "White", value: "#FFFFFF" },
    { label: "Dark", value: "#111" },
    { label: "Orange", value: "#FFF4ED" },
  ];

  const { triggerFetch: getGlobalNotifications } = useFetch({
    url: "admin/notifications/global-notification",
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const { loading: isCreateNotifiLoading, triggerFetch: createGlobalNotification } = useFetch({
    url: "admin/notifications/global-notification",
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const { triggerFetch: deleteGlobalNotification } = useFetch({
    url: `admin/notifications/global-notification`,
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const getAllGlobalNotifications = async () => {
    const { data } = await getGlobalNotifications();
    setGlobalNotifications(data ? data : []);
  };

  const handlePreviewImageChange = (imagePath: File | string) => {
    setPreviewImage(imagePath);
    setIsImagesModalOpen(false);
  };

  const handleGlobalNotificationBtnClick = async () => {
    if (!selectedTime) {
      toast.error(t("emptyDateFieldError"));
      return;
    }

    if (isButtonVisible && buttonLink) {
      const externalLink = /^(https?:\/\/)/.test(buttonLink);
      const internalLink = /^\/[A-Za-z0-9/_-]*$/.test(buttonLink);

      if (!externalLink && !internalLink) {
        toast.error(t("invalidNotifiButtonLink"));
        return;
      }
    }

    const formData = new FormData();

    formData.append("title", title);
    formData.append("description", description);
    formData.append("background", background);
    formData.append("image", previewImage);
    formData.append("button[isVisible]", String(isButtonVisible));
    formData.append("button[label]", isButtonVisible ? buttonText : "");
    formData.append("button[link]", isButtonVisible ? buttonLink : "");
    formData.append("startDate", selectedTime.format());

    try {
      const result = await createGlobalNotification(formData);
      if (result.success) {
        await getAllGlobalNotifications();
        toast.success(t("successfulGlobalNotificationCreation"));
      } else {
        toast.error(t("unsuccessfulGlobalNotificationCreation"));
      }
    } catch (error) {
      toast.error(t("unsuccessfulGlobalNotificationCreation"));
    }
  };

  const handleGlobalNotificationDelete = async (id: string) => {
    if (!id) {
      return;
    }
    try {
      await deleteGlobalNotification({ id });
      await getAllGlobalNotifications();
      toast.success(t("successfulGlobalNotificationDeleted"));
    } catch (error) {
      toast.error(t("unsuccessfulGlobalNotificationDeleted"));
    }
  };

  useEffect(() => {
    void getAllGlobalNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "24px" }}>
        <NotificationsActiveIcon sx={{ width: "36px", height: "36px", color: "#6D6777" }} />
        <Typography
          sx={{
            fontFamily: "var(--cygreRegular), sans-serif",
            fontSize: "20px",
            fontWeight: 500,
            color: "#2E263D",
          }}
        >
          {t("notifications")}
        </Typography>
      </Box>
      <IndividualNotifications />
      <Typography
        sx={{
          fontFamily: "var(--cygreMedium), sans-serif",
          color: "#2E263D",
          fontSize: "18px",
          lineHeight: "32px",
          marginBottom: "24px",
        }}
      >
        {t("globalNotifications")}
      </Typography>
      {globalNotifications.length > 0 && (
        <GlobalNotificationsList
          globalNotificationsArray={globalNotifications}
          deleteGlobalNotification={handleGlobalNotificationDelete}
        />
      )}
      <Typography
        sx={{
          fontFamily: "var(--cygreMedium), sans-serif",
          color: "#2E263D",
          fontSize: "16px",
          lineHeight: 1.22,
          textAlign: "center",
          marginBottom: "24px",
        }}
      >
        {t("createGlobalNotification")}
      </Typography>
      <CustomInput
        sx={{ marginBottom: "24px" }}
        required
        fullWidth
        id="title"
        placeholder={t("enterHeadline")}
        name="title"
        value={title}
        height="40px"
        label={t("headline")}
        customBgColor="#F6F7FB"
        onChange={(event) => {
          setTitle(event.target.value);
        }}
      />
      <CustomInput
        sx={{ marginBottom: "24px" }}
        required
        fullWidth
        id="global-notification-text"
        placeholder={t("enterNotificationText")}
        multiline
        rows={4}
        label={t("notificationText")}
        value={description}
        onChange={(event) => {
          setDescription(event.target.value);
        }}
        customBgColor="#F6F7FB"
      />
      <Box sx={{ display: "flex", gap: "10px" }}>
        <CustomSelectComponent
          options={backgroudOptions}
          name="backgroud"
          value={background}
          title={t("backgroudColor")}
          defaultValue={background}
          onChange={(event) => {
            setBackground(event.target.value as string);
          }}
          noBorder
          backgroundColor="#F6F7FB"
        />
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
          <Typography
            sx={{
              fontFamily: "var(--cygreMedium), sans-serif",
              color: "#2E263D",
              fontSize: "12px",
              lineHeight: 1.22,
              textAlign: "left",
              marginBottom: "10px",
            }}
          >
            {t("picture")}
          </Typography>
          <CustomButton
            width="100%"
            height={40}
            textButton={t("changePicture")}
            customColor="#6D6777"
            customBorderColor="#C9D0D8"
            customHoverColor="transparent"
            customBorderRadius="8px"
            customFontSize="14px"
            customTextTransform="none"
            onClick={() => setIsImagesModalOpen(true)}
          />
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "32px", marginTop: "24px", marginBottom: "24px" }}>
        <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", width: "max-content" }}>
          <Typography
            sx={{
              fontFamily: "var(--cygreMedium), sans-serif",
              color: "#2E263D",
              fontSize: "12px",
              lineHeight: 1.22,
              textAlign: "left",
              marginBottom: "12px",
            }}
          >
            {t("showButton")}
          </Typography>
          <CustomSwitch checked={isButtonVisible} onChange={(event) => setIsButtonVisible(event.target.checked)} />
        </Box>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: "24px", lg: "10px" },
          }}
        >
          <CustomInput
            required
            fullWidth
            id="buttonLabel"
            name="buttonLabel"
            value={buttonText}
            height="40px"
            label={t("buttonLabel")}
            customBgColor="#F6F7FB"
            onChange={(event) => {
              setButtonText(event.target.value);
            }}
            disabled={!isButtonVisible}
          />
          <CustomInput
            required
            fullWidth
            id="buttonLink"
            name="buttonLink"
            value={buttonLink}
            height="40px"
            label={t("link")}
            placeholder={t("enterLinkPush")}
            customBgColor="#F6F7FB"
            onChange={(event) => {
              setButtonLink(event.target.value);
            }}
            disabled={!isButtonVisible}
          />
        </Box>
      </Box>
      <Typography
        sx={{
          fontFamily: "var(--cygreMedium), sans-serif",
          color: "#2E263D",
          fontSize: "12px",
          lineHeight: 1.22,
          textAlign: "left",
          marginBottom: "10px",
        }}
      >
        {t("globalNotificationDate")}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: "10px", marginBottom: "32px" }}>
        <Box sx={{ width: { xs: "100%", md: 172 } }}>
          <CustomDatePicker
            slots={{
              openPickerIcon: Calendar,
            }}
            value={selectedTime}
            onChange={(newValue) => setSelectedTime(newValue)}
            slotProps={{
              textField: {
                inputProps: { disabled: true },
              },
            }}
            minDate={dayjs()}
          />
        </Box>

        <Box sx={{ width: { xs: "100%", md: 172 } }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker value={selectedTime} onChange={setSelectedTime} />
          </LocalizationProvider>
        </Box>
        <CustomButton
          textButton={t("create")}
          height={40}
          width={isTablet ? "100%" : "172px"}
          customBgColor="#F4701B"
          customHoverColor="#F4701B"
          customBorderRadius="8px"
          customFontSize="14px"
          customTextTransform="none"
          onClick={handleGlobalNotificationBtnClick}
          loading={isCreateNotifiLoading}
        />
      </Box>
      <GlobalNotificationPreview
        description={description}
        background={background}
        title={title}
        isButtonVisible={isButtonVisible}
        buttonText={buttonText}
        buttonLink={buttonLink}
        previewImage={previewImage}
      />
      <GlobalNotificationsImagesModal
        isModalOpen={isImagesModalOpen}
        setIsModalOpen={setIsImagesModalOpen}
        setPreviewImage={handlePreviewImageChange}
        previewImagesArray={previewImagesArray}
      />
    </LocalizationProvider>
  );
};

export default NotificationsPage;
